import React from 'react';
import type {SetRequired} from 'type-fest';
import {type FieldValues, type FieldPath} from 'react-hook-form';
import {splitEventHandler} from '@netx/core-next/utils';
import {useConstCallback} from '@netx/core-components/hooks/useEvent';
import {combineRefs} from '@netx/core-components/util';
import {Checkbox, type CheckboxProps} from '@netx/core-components/Checkbox';
import {
	ReactHookFormControl,
	type WithReactHookFormControlFieldProps,
} from '../ReactHookFormControl';

export interface ReactHookFormCheckboxProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends WithReactHookFormControlFieldProps<
		SetRequired<Omit<CheckboxProps, 'checked'>, 'name'>,
		TFieldValues,
		TName
	> {}

export const ReactHookFormCheckboxPrimitive = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	{
		control,
		ControllerProps,
		...props
	}: ReactHookFormCheckboxProps<TFieldValues, TName>,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	return (
		<ReactHookFormControl<TFieldValues, TName>
			{...ControllerProps}
			control={control}
			name={props.name as TName}
			render={({field, formState}) => (
				<Checkbox
					error={formState.errors[props.name]?.message?.toString()}
					{...props}
					{...field}
					checked={field.value}
					disabled={field.disabled || props.disabled}
					ref={ref}
					inputRef={combineRefs(props.inputRef, field.ref)}
					onChange={useConstCallback(
						splitEventHandler(props.onChange, (event, checked) =>
							field.onChange(checked),
						),
					)}
				/>
			)}
			rules={{
				required: props.required,
				validate: async (value) => {
					return props.required && !value ? i18n.t('i18n.required') : true;
				},
				...ControllerProps?.rules,
			}}
		/>
	);
};

/**
 * `react-hook-form` controlled `Checkbox` component
 */
export const ReactHookFormCheckbox = React.forwardRef(
	ReactHookFormCheckboxPrimitive,
) as <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	props: ReactHookFormCheckboxProps<TFieldValues, TName>,
) => JSX.Element;

export default ReactHookFormCheckbox;
